import React, { useState, useEffect } from 'react';
import { 
  X, Download, Printer, Search, RefreshCw, FileText, Filter, Book, Clock, Calendar
} from 'lucide-react';
import '../styles/ReportsForm.css';
import apiClient from '../services/api';
import { useNavigate } from 'react-router-dom';

const LedgerReport = ({ onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filter state
  const [filters, setFilters] = useState({
    AccountID: '',
    StartDate: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
    EndDate: new Date().toISOString().slice(0, 10)
  });
  
  // Report data
  const [reportData, setReportData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Session
  const session = getSessionData();
  
  function getSessionData() {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session:', error);
      return null;
    }
  }
  
  useEffect(() => {
    loadAccounts();
  }, []);
  
  const loadAccounts = async () => {
    try {
      const response = await apiClient.get('/AccountHeads.php');
      if (response.data.success) {
        setAccounts(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  const handleGenerateReport = async () => {
    if (!filters.AccountID) {
      setError('Please select an account');
      return;
    }
    
    if (!filters.StartDate || !filters.EndDate) {
      setError('Please select date range');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await apiClient.get('/LedgerReport.php', { params: filters });
      
      if (response.data.success) {
        setReportData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to generate report');
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Error generating report: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handlePrint = () => {
    window.print();
  };
  
  const handleExportCSV = () => {
    if (!reportData || !reportData.transactions || reportData.transactions.length === 0) {
      alert('No data to export');
      return;
    }
    
    const headers = ['Date', 'Voucher No', 'Type', 'Particulars', 'Debit', 'Credit', 'Balance'];
    
    const rows = reportData.transactions.map(trans => [
      trans.TransDate || '',
      trans.VoucherNo || '',
      trans.VoucherType || '',
      trans.Particulars || '',
      trans.Debit || '0.00',
      trans.Credit || '0.00',
      trans.Balance || '0.00'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ledger_${reportData.account?.ACHeadCode}_${filters.StartDate}_to_${filters.EndDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleClearFilters = () => {
    setFilters({
      AccountID: '',
      StartDate: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
      EndDate: new Date().toISOString().slice(0, 10)
    });
    setReportData(null);
    setSearchTerm('');
  };
  
  const filteredAccounts = accounts.filter(acc =>
    acc.acheadname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    acc.acheadcode?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  if (!session?.companyid) {
    return (
      <div className="report-container">
        <div className="report-header">
          <div className="header-left">
            <h2>Session Required</h2>
            <p className="subtitle">Please select company and year</p>
          </div>
        </div>
        <div className="report-display">
          <div className="report-empty">
            <div className="empty-icon">🔒</div>
            <h3 className="empty-title">Session Required</h3>
            <p className="empty-message">Please select a company and financial year from the dashboard.</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="report-container">
      {/* Header */}
      <div className="report-header">
        <div className="header-left">
          <h2>Ledger Report</h2>
          <p className="subtitle">Account-wise Transaction Details</p>
        </div>
      </div>
      
      {/* Toolbar */}
      <div className="report-toolbar no-print">
        <button onClick={handleGenerateReport} disabled={loading} className="btn-generate">
          <RefreshCw size={18} />
          Generate Report
        </button>
        <button 
          onClick={handlePrint} 
          disabled={!reportData || loading}
          className="btn-print"
        >
          <Printer size={18} />
          Print
        </button>
        <button 
          onClick={handleExportCSV} 
          disabled={!reportData || loading}
          className="btn-export"
        >
          <Download size={18} />
          Export CSV
        </button>
        <button onClick={handleClearFilters} disabled={loading} className="btn-clear">
          <RefreshCw size={18} />
          Clear
        </button>
      </div>
      
      {/* Alerts */}
      {error && (
        <div className="report-alert alert-error">
          <div className="alert-icon">⚠</div>
          <div><strong>Error:</strong> {error}</div>
        </div>
      )}
      
      {/* Filters */}
      <div className="filters-section no-print">
        <h3 className="section-title">
          <Filter size={18} />
          Report Filters
        </h3>
        <div className="filters-grid">
          <div className="filter-group" style={{ gridColumn: '1 / -1' }}>
            <label>Select Account <span className="required">*</span></label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              <input
                type="text"
                placeholder="Search accounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ flex: 1 }}
              />
              <Search size={18} style={{ color: '#6b7280' }} />
            </div>
            <select
              name="AccountID"
              value={filters.AccountID}
              onChange={handleFilterChange}
              required
              size="6"
            >
              <option value="">-- Select Account --</option>
              {filteredAccounts.map(acc => (
                <option key={acc.acheadid} value={acc.acheadid}>
                  {acc.acheadcode} - {acc.acheadname} ({acc.acgroupname})
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Start Date <span className="required">*</span></label>
            <input
              type="date"
              name="StartDate"
              value={filters.StartDate}
              onChange={handleFilterChange}
              required
            />
          </div>
          
          <div className="filter-group">
            <label>End Date <span className="required">*</span></label>
            <input
              type="date"
              name="EndDate"
              value={filters.EndDate}
              onChange={handleFilterChange}
              required
            />
          </div>
        </div>
      </div>
      
      {/* Loading State */}
      {loading && (
        <div className="report-loading">
          <div className="loading-spinner"></div>
          <p className="loading-text">Generating ledger report...</p>
        </div>
      )}
      
      {/* Report Display */}
      {reportData && !loading && (
        <div className="report-display">
          {/* Report Header */}
          <div className="report-title-section">
            <h1 className="report-company-name">
              {reportData.company?.CompanyName || 'Company Name'}
            </h1>
            <h2 className="report-name">Ledger Report</h2>
            <div className="report-metadata">
              <div className="report-metadata-item">
                <Calendar size={16} />
                <span>Period: {new Date(filters.StartDate).toLocaleDateString()} to {new Date(filters.EndDate).toLocaleDateString()}</span>
              </div>
              <div className="report-metadata-item">
                <Clock size={16} />
                <span>Generated: {new Date().toLocaleString()}</span>
              </div>
            </div>
          </div>
          
          {/* Account Header */}
          <div className="ledger-header">
            <div className="ledger-account-info">
              <h2 className="ledger-account-name">{reportData.account?.ACHeadName}</h2>
              <div className="ledger-account-code">{reportData.account?.ACHeadCode}</div>
              <div style={{ marginTop: '8px', fontSize: '14px', color: '#6b7280', fontWeight: '600' }}>
                Group: {reportData.account?.GroupName}
              </div>
            </div>
          </div>
          
          {/* Opening Balance Card */}
          <div className="summary-cards" style={{ marginBottom: '24px' }}>
            <div className={`summary-card ${reportData.openingBalance?.Type === 'Dr' ? 'green' : 'red'}`}>
              <div className="summary-card-label">Opening Balance</div>
              <div className="summary-card-value">
                {parseFloat(reportData.openingBalance?.Amount || 0).toFixed(2)} {reportData.openingBalance?.Type || 'Dr'}
              </div>
            </div>
          </div>
          
          {/* Transactions Table */}
          {reportData.transactions && reportData.transactions.length > 0 ? (
            <div className="report-table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th style={{ width: '100px' }}>Date</th>
                    <th style={{ width: '120px' }}>Voucher No</th>
                    <th style={{ width: '60px' }}>Type</th>
                    <th>Particulars</th>
                    <th className="text-right" style={{ width: '120px' }}>Debit</th>
                    <th className="text-right" style={{ width: '120px' }}>Credit</th>
                    <th className="text-right" style={{ width: '140px' }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="ledger-balance-row">
                    <td colSpan="4" style={{ fontWeight: '700', textTransform: 'uppercase' }}>Opening Balance</td>
                    <td className="text-right">
                      {reportData.openingBalance?.Type === 'Dr' ? parseFloat(reportData.openingBalance.Amount || 0).toFixed(2) : '-'}
                    </td>
                    <td className="text-right">
                      {reportData.openingBalance?.Type === 'Cr' ? parseFloat(reportData.openingBalance.Amount || 0).toFixed(2) : '-'}
                    </td>
                    <td className="text-right" style={{ fontWeight: '700' }}>
                      {parseFloat(reportData.openingBalance?.Amount || 0).toFixed(2)} 
                      <span className={`balance-badge ${reportData.openingBalance?.Type === 'Dr' ? 'dr' : 'cr'}`} style={{ marginLeft: '8px' }}>
                        {reportData.openingBalance?.Type}
                      </span>
                    </td>
                  </tr>
                  {reportData.transactions.map((trans, index) => (
                    <tr key={index}>
                      <td style={{ fontSize: '13px' }}>
                        {new Date(trans.TransDate).toLocaleDateString()}
                      </td>
                      <td>
                        <span className="account-code" style={{ fontSize: '12px' }}>
                          {trans.VoucherNo}
                        </span>
                      </td>
                      <td>
                        <span className={`voucher-badge ${trans.VoucherType.toLowerCase()}`}>
                          {trans.VoucherType}
                        </span>
                      </td>
                      <td className="particulars">
                        {trans.Particulars}
                        {trans.Remarks && (
                          <div className="particulars-remarks">
                            {trans.Remarks}
                          </div>
                        )}
                      </td>
                      <td className="text-right">
                        {parseFloat(trans.Debit || 0) > 0 ? (
                          <span className="amount-debit">{parseFloat(trans.Debit).toFixed(2)}</span>
                        ) : (
                          <span style={{ color: '#d1d5db' }}>-</span>
                        )}
                      </td>
                      <td className="text-right">
                        {parseFloat(trans.Credit || 0) > 0 ? (
                          <span className="amount-credit">{parseFloat(trans.Credit).toFixed(2)}</span>
                        ) : (
                          <span style={{ color: '#d1d5db' }}>-</span>
                        )}
                      </td>
                      <td className="text-right" style={{ fontWeight: '700' }}>
                        {parseFloat(trans.Balance || 0).toFixed(2)} 
                        <span className={`balance-badge ${trans.BalanceType === 'Dr' ? 'dr' : 'cr'}`} style={{ marginLeft: '8px' }}>
                          {trans.BalanceType}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="4" className="total-label">Totals:</td>
                    <td className="text-right total-amount amount-debit">
                      {parseFloat(reportData.totals?.TotalDebit || 0).toFixed(2)}
                    </td>
                    <td className="text-right total-amount amount-credit">
                      {parseFloat(reportData.totals?.TotalCredit || 0).toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                  <tr>
                    <td colSpan="6" className="total-label">Closing Balance:</td>
                    <td className="text-right total-amount" style={{ fontSize: '18px' }}>
                      {parseFloat(reportData.closingBalance?.Amount || 0).toFixed(2)} 
                      <span className={`balance-badge ${reportData.closingBalance?.Type === 'Dr' ? 'dr' : 'cr'}`} style={{ marginLeft: '8px' }}>
                        {reportData.closingBalance?.Type}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="report-empty">
              <div className="empty-icon">📖</div>
              <h3 className="empty-title">No Transactions Found</h3>
              <p className="empty-message">No transactions found for the selected period</p>
            </div>
          )}
          
          {/* Summary Cards */}
          {reportData.totals && (
            <div className="summary-cards" style={{ marginTop: '24px' }}>
              <div className="summary-card green">
                <div className="summary-card-label">Total Debit</div>
                <div className="summary-card-value">{parseFloat(reportData.totals.TotalDebit || 0).toFixed(2)}</div>
              </div>
              
              <div className="summary-card red">
                <div className="summary-card-label">Total Credit</div>
                <div className="summary-card-value">{parseFloat(reportData.totals.TotalCredit || 0).toFixed(2)}</div>
              </div>
              
              <div className={`summary-card ${reportData.closingBalance?.Type === 'Dr' ? 'green' : 'red'}`}>
                <div className="summary-card-label">Closing Balance</div>
                <div className="summary-card-value">
                  {parseFloat(reportData.closingBalance?.Amount || 0).toFixed(2)} {reportData.closingBalance?.Type}
                </div>
              </div>
            </div>
          )}
          
          {/* Signature Section */}
          <div className="signature-section print-only">
            <div className="signature-box">
              <div className="signature-line">Prepared By</div>
            </div>
            <div className="signature-box">
              <div className="signature-line">Checked By</div>
            </div>
            <div className="signature-box">
              <div className="signature-line">Approved By</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LedgerReport;